import { ThemeType } from 'types';
import { hexToRGB } from 'utils';
import { css } from 'styled-components';
import { BASE_COLORS } from './constants';
import { darkColorsTheme, antDarkColorsTheme } from './dark/darkTheme';
import { lightColorsTheme } from './light/lightTheme';

export const themeObject = {
  light: lightColorsTheme,
  dark: darkColorsTheme,
};

export const antThemeObject = {
  light: {},
  dark: antDarkColorsTheme,
};

const getThemeVariables = (theme: ThemeType) => css`
  color-scheme: ${theme};
  --brand-primary-color: ${themeObject[theme].semantic.brandPrimary};
  --brand-primary-hover-color: ${themeObject[theme].semantic.brandPrimaryHover};
  --brand-accent-color: ${themeObject[theme].semantic.brandAccent};
  --navigation-surface-color: ${themeObject[theme].semantic.navigationSurface};
  --navigation-text-color: ${themeObject[theme].semantic.navigationText};

  --surface-page-color: ${themeObject[theme].semantic.surfacePage};
  --surface-container-color: ${themeObject[theme].semantic.surfaceContainer};
  --surface-elevated-color: ${themeObject[theme].semantic.surfaceElevated};
  --surface-muted-color: ${themeObject[theme].semantic.surfaceMuted};
  --surface-hover-color: ${themeObject[theme].semantic.surfaceHover};
  --surface-selected-color: ${themeObject[theme].semantic.surfaceSelected};
  --surface-disabled-color: ${themeObject[theme].semantic.surfaceDisabled};

  --text-primary-color: ${themeObject[theme].semantic.textPrimary};
  --text-secondary-content-color: ${themeObject[theme].semantic.textSecondary};
  --text-muted-color: ${themeObject[theme].semantic.textMuted};
  --text-disabled-color: ${themeObject[theme].semantic.textDisabled};
  --text-on-brand-color: ${themeObject[theme].semantic.textOnBrand};
  --text-on-light-color: ${themeObject[theme].semantic.textOnLight};

  --border-subtle-color: ${themeObject[theme].semantic.borderSubtle};
  --border-strong-color: ${themeObject[theme].semantic.borderStrong};
  --focus-ring-color: ${themeObject[theme].semantic.focusRing};
  --overlay-scrim-color: ${themeObject[theme].semantic.overlayScrim};
  --shadow-elevated: ${themeObject[theme].semantic.shadowElevated};
  --shadow-overlay: ${themeObject[theme].semantic.shadowOverlay};

  --status-success-text-color: ${themeObject[theme].semantic.statusSuccessText};
  --status-success-surface-color: ${themeObject[theme].semantic
    .statusSuccessSurface};
  --status-success-border-color: ${themeObject[theme].semantic
    .statusSuccessBorder};
  --status-warning-text-color: ${themeObject[theme].semantic.statusWarningText};
  --status-warning-surface-color: ${themeObject[theme].semantic
    .statusWarningSurface};
  --status-warning-border-color: ${themeObject[theme].semantic
    .statusWarningBorder};
  --status-error-text-color: ${themeObject[theme].semantic.statusErrorText};
  --status-error-surface-color: ${themeObject[theme].semantic
    .statusErrorSurface};
  --status-error-border-color: ${themeObject[theme].semantic.statusErrorBorder};
  --status-info-text-color: ${themeObject[theme].semantic.statusInfoText};
  --status-info-surface-color: ${themeObject[theme].semantic.statusInfoSurface};
  --status-info-border-color: ${themeObject[theme].semantic.statusInfoBorder};
  --highlight-surface-color: ${themeObject[theme].semantic.highlightSurface};
  --highlight-text-color: ${themeObject[theme].semantic.highlightText};
  --auth-panel-background-color: ${themeObject[theme].semantic
    .authPanelBackground};
  --schedule-tag-background-color: ${themeObject[theme].semantic
    .scheduleTagBackground};
  --export-action-color: ${themeObject[theme].semantic.exportAction};

  --primary-color: ${themeObject[theme].primary};
  --primary1-color: ${themeObject[theme].primary1};
  --primary-gradient-color: ${themeObject[theme].primaryGradient};
  --info-color: var(--primary-color);
  --secondary-color: ${themeObject[theme].secondary};
  --error-color: ${themeObject[theme].error};
  --medium-error-color: ${themeObject[theme].mediumErrorColor};
  --dark-error-color: ${themeObject[theme].darkError};
  --warning-color: ${themeObject[theme].warning};
  --success-color: ${themeObject[theme].success};
  --background-color: ${themeObject[theme].background};
  --secondary-background-color: ${themeObject[theme].secondaryBackground};
  --secondary-background-selected-color: ${themeObject[theme]
    .secondaryBackgroundSelected};
  --additional-background-color: ${themeObject[theme].additionalBackground};
  --collapse-background-color: ${themeObject[theme].collapseBackground};
  --timeline-background-color: ${themeObject[theme].timelineBackground};
  --spinner-base-color: ${themeObject[theme].spinnerBase};
  --sider-background-color: ${themeObject[theme].siderBackground};
  --shadow-color: ${themeObject[theme].shadow};
  --border-color: ${themeObject[theme].border};
  --border-nft-color: ${themeObject[theme].borderNft};
  --scroll-color: ${themeObject[theme].scroll};

  --primary-rgb-color: ${hexToRGB(themeObject[theme].primary)};
  --info-rgb-color: ${hexToRGB(themeObject[theme].primary)};
  --secondary-rgb-color: ${hexToRGB(themeObject[theme].secondary)};
  --error-rgb-color: ${hexToRGB(themeObject[theme].error)};
  --warning-rgb-color: ${hexToRGB(themeObject[theme].warning)};
  --success-rgb-color: ${hexToRGB(themeObject[theme].success)};
  --background-rgb-color: ${hexToRGB(themeObject[theme].background)};

  --text-main-color: ${themeObject[theme].textMain};
  --text-light-color: ${themeObject[theme].textLight};
  --text-superLight-color: ${themeObject[theme].textSuperLight};
  --text-secondary-color: ${themeObject[theme].textSecondary};
  --text-dark-color: ${themeObject[theme].textDark};
  --text-nft-light-color: ${themeObject[theme].textNftLight};
  --text-sider-primary-color: ${themeObject[theme].textSiderPrimary};
  --text-sider-secondary-color: ${themeObject[theme].textSiderSecondary};
  --subtext-color: ${themeObject[theme].subText};

  --dashboard-map-background-color: ${themeObject[theme]
    .dashboardMapBackground};
  --dashboard-map-circle-color: ${themeObject[theme].dashboardMapCircleColor};
  --dashboard-map-control-disabled-background-color: ${themeObject[theme]
    .dashboardMapControlDisabledBackground};

  --notification-success-color: ${themeObject[theme].notificationSuccess};
  --notification-primary-color: ${themeObject[theme].notificationPrimary};
  --notification-warning-color: ${themeObject[theme].notificationWarning};
  --notification-error-color: ${themeObject[theme].notificationError};

  --icon-color: ${themeObject[theme].icon};
  --icon-hover-color: ${themeObject[theme].iconHover};
  --box-shadow: ${themeObject[theme].boxShadow};
  --box-shadow-hover: ${themeObject[theme].boxShadowHover};
  --box-shadow-nft-color: ${themeObject[theme].boxShadowNft};
  --box-shadow-nft-secondary-color: ${themeObject[theme].boxShadowNftSecondary};

  --heading-color: ${themeObject[theme].heading};
  --item-hover-bg: ${themeObject[theme].itemHoverBg};
  --background-base-color: ${themeObject[theme].backgroundColorBase};
  --border-base-color: ${themeObject[theme].borderBase};
  --disabled-color: ${themeObject[theme].disable};
  --disabled-bg-color: ${themeObject[theme].disabledBg};
  --layout-body-bg-color: ${themeObject[theme].layoutBodyBg};
  --layout-header-bg-color: ${themeObject[theme].layoutHeaderBg};
  --layout-sider-bg-color: ${themeObject[theme].layoutSiderBg};
  --input-placeholder-color: ${themeObject[theme].inputPlaceholder};
  --avatar-bg: ${themeObject[theme].avatarBg};
  --alert-text-color: ${themeObject[theme].alertTextColor};
  --breadcrumb-color: ${themeObject[theme].breadcrumb};
`;

export const lightThemeVariables = css`
  ${getThemeVariables('light')}
`;

export const darkThemeVariables = css`
  ${getThemeVariables('dark')}
  --ant-success-color-deprecated-bg: ${antThemeObject['dark']
    .successBg} !important;
  --ant-success-color-deprecated-border: ${antThemeObject['dark']
    .successBorder} !important;
`;

export const commonThemeVariables = css`
  color-scheme: light dark;
  --white: ${BASE_COLORS.white};
  --black: ${BASE_COLORS.black};
  --green: ${BASE_COLORS.green};
  --orange: ${BASE_COLORS.orange};
  --gray: ${BASE_COLORS.gray};
  --lightgrey: ${BASE_COLORS.lightgrey};
  --violet: ${BASE_COLORS.violet};
  --lightgreen: ${BASE_COLORS.lightgreen};
  --pink: ${BASE_COLORS.pink};
  --blue: ${BASE_COLORS.blue};
  --skyblue: ${BASE_COLORS.skyblue};
  --red: ${BASE_COLORS.red};
`;

export const antOverrideCssVariables = css`
  --ant-primary-1: var(--primary1-color) !important;
`;
